import { Outlet } from "@tanstack/react-router";

import { useCurrentUser } from "~/lib/auth";
import { LoginPage } from "~/routes/login";

/**
 * AuthGate wraps the entire SPA. It runs the /me probe once at boot, then:
 *   - while loading: shows a minimal splash so the shell never flashes
 *   - if 401 (no user): renders the LoginPage
 *   - if a user is present: renders the routed app shell + outlet
 *
 * After login/logout the auth store invalidates the cached user, which
 * re-renders this component into the right branch automatically.
 */
export function AuthGate(): React.ReactElement {
    const { data: user, isLoading, isError, error } = useCurrentUser();

    if (isLoading) {
        return <BootSplash label="Connecting…" />;
    }

    if (isError) {
        return (
            <BootSplash
                label="Cannot reach the IntelRoom backend."
                detail={error instanceof Error ? error.message : "Unknown error"}
                tone="danger"
            />
        );
    }

    if (!user) {
        return <LoginPage />;
    }

    return <Outlet />;
}

interface BootSplashProps {
    label: string;
    detail?: string;
    tone?: "default" | "danger";
}

function BootSplash({ label, detail, tone = "default" }: BootSplashProps): React.ReactElement {
    return (
        <div
            style={{
                display: "grid",
                placeItems: "center",
                minHeight: "100dvh",
                background: "var(--color-bg)",
                color: "var(--color-text)",
                padding: "var(--space-6)",
            }}
        >
            <div className="stack-sm" style={{ alignItems: "center", textAlign: "center" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)" }}>
                    <span
                        className="brandmark__dot live-dot"
                        aria-hidden
                        style={{
                            background: tone === "danger" ? "var(--color-danger)" : "var(--color-accent)",
                        }}
                    />
                    <span className="type-h2" style={{ letterSpacing: "var(--letter-spacing-tight)" }}>
                        IntelRoom
                    </span>
                </div>
                <div className="text-text-muted">{label}</div>
                {detail && <div className="text-12 text-text-subtle" style={{ maxWidth: 480 }}>{detail}</div>}
            </div>
        </div>
    );
}
